import React from 'react';
import {
  Box,
  Card,
  Checkbox,
  Input,
  Label,
  Title,
} from '@nimbus-ds/components';
import { FormField } from '@nimbus-ds/patterns';
import { InterfaceNameValue } from '@tiendanube/components';
import { PersonalDataState } from 'domains/PagoNubeOnboarding/Onboarding/types';
import { addressDataElements } from 'domains/PagoNubeOnboarding/Onboarding/utils';
import useTranslationPagoNubeOnboarding from 'domains/PagoNubeOnboarding/useTranslationPagoNubeOnboarding';

interface PlaceOfResidenceDataProps {
  errors: Partial<Record<string, string>>;
  handleOnBlur: (data: InterfaceNameValue) => void;
  handleChangeAddressesData: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    property: keyof PersonalDataState,
  ) => void;
  handleChangeStreetNumber: (
    e: React.ChangeEvent<HTMLInputElement>,
    streetNumber: number,
  ) => void;
  isLoading: boolean;
  isError: boolean;
  isWithoutStreetNumberChecked: boolean;
  personalDataState: PersonalDataState;
}

function PlaceOfResidenceData({
  errors,
  handleOnBlur,
  handleChangeAddressesData,
  handleChangeStreetNumber,
  isError,
  isLoading,
  isWithoutStreetNumberChecked,
  personalDataState,
}: Readonly<PlaceOfResidenceDataProps>) {
  const t = useTranslationPagoNubeOnboarding('placeOfResidenceData');

  return (
    <Box marginBottom="2">
      <Card>
        <Title as="h3">{t('title')}</Title>
        <FormField.Input
          aria-labelledby="street"
          appearance={errors['street'] ? 'danger' : 'none'}
          label={t('street')}
          type="text"
          property="street"
          value={personalDataState.street}
          onBlur={(e) =>
            handleOnBlur({ name: 'street', value: e.target.value })
          }
          onChange={(
            e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
          ) => handleChangeAddressesData(e, 'street')}
          helpText={errors['street']}
          showHelpText={!!errors['street']}
        />
        <FormField.Input
          appearance={errors['streetNumber'] ? 'danger' : 'none'}
          label={t('streetNumber')}
          disabled={isWithoutStreetNumberChecked}
          type="text"
          property="streetNumber"
          value={personalDataState.streetNumber}
          onBlur={(e) =>
            handleOnBlur({ name: 'streetNumber', value: e.target.value })
          }
          onChange={(
            e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
          ) => handleChangeAddressesData(e, 'streetNumber')}
          helpText={errors['streetNumber']}
          showHelpText={!!errors['streetNumber']}
        />
        <Checkbox
          name="withoutNumber"
          label={t('withoutNumber')}
          checked={isWithoutStreetNumberChecked}
          onChange={(e) =>
            handleChangeStreetNumber(e, personalDataState.streetNumber ?? 0)
          }
        />
        {addressDataElements.map((element) => {
          const value = personalDataState[element.key] || '';
          const error = element.key === 'zipCode' && isError;
          return element.disabled && isLoading ? (
            <Box key={element.key}>
              <Label>{t(element.key)}</Label>
              <Input.Skeleton />
            </Box>
          ) : (
            <FormField.Input
              key={element.key}
              appearance={error || !!errors[element.key] ? 'danger' : 'none'}
              label={t(element.key)}
              type={element.type}
              onBlur={(e) =>
                handleOnBlur({ name: element.key, value: e.target.value })
              }
              onChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
              ) => handleChangeAddressesData(e, element.key)}
              property={element.key}
              value={value}
              disabled={element.disabled || isLoading}
              helpText={errors[element.key]}
              showHelpText={!!errors[element.key]}
            />
          );
        })}
      </Card>
    </Box>
  );
}

export default PlaceOfResidenceData;
